<template>
  <div class="sg-dropdown" v-click-outside:[dropdownRef]="onClickOutside">
    <details role="list" dir="ltr" :open="open" ref="dropdownRef">
      <summary
        aria-haspopup="listbox"
        ref="dropDownTextRef"
        :class="dropdownTriggerKls"
        @click="onClick"
        @contextmenu="onContextMenu"
        @mouseenter="onMouseenter"
        @mouseleave="onMouseleave"
      >
        <slot>
          {{ title }}
          <sg-icon class="sg-icon--right">
            <ArrowDown />
          </sg-icon>
        </slot>
      </summary>
      <teleport :disabled="!transfer" to="body">
        <ul
          role="listbox"
          :class="[ns.e('popper'), ns.b('content'), ns.is(isSimple ? 'simple' : ''), dropClassName]"
          :style="contentStyle"
          ref="dropDownContentRef"
          @mouseenter="onMouseenter"
          @mouseleave="onMouseleave"
        >
          <template v-if="transfer">
            <template v-if="open">
              <div v-if="isSimple" class="popper-arrow" data-popper-arrow></div>
              <slot name="menu">
              </slot>
            </template>
          </template>
          <template v-else>
            <div v-if="isSimple" class="popper-arrow" data-popper-arrow></div>
            <slot name="menu">
              {{ title }}
            </slot>
          </template>
        </ul>
      </teleport>
    </details>
  </div>
</template>

<script lang="ts">
import {
  ref,
  unref,
  watch,
  computed,
  onMounted,
  provide,
  defineComponent,
  CSSProperties
} from 'vue'
import { dropdownProps, DropdownContextKey } from './dropdown'
import { createPopper } from '@popperjs/core'
import { unwrapMeasurableEl } from './../../popper/src/utils'
import { useDelayedToggle, useModelToggle, useSize } from '@sgui-plus/hooks'
import { useNamespace, useZIndex } from '@sgui-plus/hooks'
import { ClickOutside } from '@sgui-plus/directives'
import { SgIcon } from '@sgui-plus/components/icon'
import { ArrowDown } from '@sgui-plus/icons-vue'

export default defineComponent({
  name: 'SgDropdown',
  props: dropdownProps,
  components: {
    SgIcon,
    ArrowDown
  },
  directives: {
    'ClickOutside': ClickOutside
  },
  emits: ['on-visible-change', 'on-click', 'on-clickoutside'],
  setup(props, { emit, expose }) {
    const ns = useNamespace('dropdown')
    const { nextZIndex } = useZIndex()
    let instance: ReturnType<typeof createPopperInstance> | null = null
    const contentZIndex = ref(nextZIndex())
    const open = ref(false)
    const dropdownItemMouseEnter = ref(false)

    const dropdownRef = ref<HTMLElement | null>(null)
    const dropDownTextRef = ref<HTMLElement | null>(null)
    const dropDownContentRef = ref<HTMLElement | null>(null)
    const contentStyle = computed<CSSProperties>(
      () => [{
        minWidth: props.calculateDropdownMinWidth
          ? dropDownTextRef.value
            ? dropDownTextRef.value?.clientWidth + 'px'
            : 'auto'
          : 'auto',
        maxHeight: props.maxHeight,
        overflow: 'inherit',
        zIndex: unref(contentZIndex)
      }] as any
    )

    const isSimple = computed<Boolean>(() => {
      return props.dropdownStyle === 'simple'
    })

    const dropdownSize = useSize()
    const dropdownTriggerKls = computed(() => [
      [dropdownSize.value ? `sg-dropdown--${dropdownSize.value}` : '', ns.e('trigger')],
    ])

    const createPopperInstance = ({
      referenceEl,
      popperContentEl,
    }) => {
      return createPopper(referenceEl, popperContentEl, {
        placement: props.placement,
        modifiers: [{
          name: 'offset',
          options: {
            offset: [0, 2]
          }
        }]
      })
    }
    function updatePopper() {
      contentZIndex.value = nextZIndex()
    }

    function onClick(event) {
      event?.preventDefault()
      if (isTargetTrigger(['click']) || isTargetTrigger(['contextMenu']) && open.value)
        onTrigger()
    }
    function onContextMenu(event) {
      if (!isTargetTrigger(['contextMenu'])) return
      event?.preventDefault()
      onTrigger()
    }
    function onMouseenter() {
      dropdownItemMouseEnter.value = true
      if (!isTargetTrigger(['hover'])) return
      onOpen()
    }
    function onMouseleave() {
      dropdownItemMouseEnter.value = false
      if (!isTargetTrigger(['hover'])) return
      onClose()
    }

    const { show, hide } = useModelToggle({
      indicator: open,
      onShow: () => {
        popperRefresh()
      }
    })

    const { onOpen, onClose } = useDelayedToggle({
      showAfter: ref(0),
      hideAfter: ref(200),
      autoClose: ref(0),
      open: show,
      close: hide
    })

    function isTargetTrigger(types) {
      if (isTriggerCustom()) return false
      return types.includes(props.trigger)
    }

    function popperRefresh() {
      const timer = setTimeout(() => {
        instance?.update()
        clearTimeout(timer)
      })
    }

    function onTrigger() {
      !open.value ? onOpen() : onClose()
    }

    function onClickItem (val) {
      emit('on-click', val)
      if (!props.hideAfterClick || isTriggerCustom()) return
      onClose()
    }

    function onClickOutside (event) {
      if (!isTriggerCustom() || dropdownItemMouseEnter.value) return
      emit('on-clickoutside', event)
    }

    function isTriggerCustom () {
      return props.trigger === 'custom'
    }

    provide(DropdownContextKey, {
      onClickItem
    })

    watch(() => open.value, () => {
      emit('on-visible-change', open.value)
    })

    watch(
      () => props.visible,
      (val) => {
        val ? onOpen() : onClose()
      }
    )

    onMounted(() => {
      let updateHandle: ReturnType<typeof watch>
      watch(
        () => unwrapMeasurableEl(dropDownTextRef.value),
        (val) => {
          updateHandle?.()
          if (val) {
            const popperContentEl = unref(dropDownContentRef)!
            instance = createPopperInstance({
              referenceEl: val,
              popperContentEl,
            })

            updateHandle = watch(
              () => val!.getBoundingClientRect(),
              () => {
                updatePopper()
              },
              {
                immediate: true,
              }
            )
          } else {
            dropDownContentRef.value = null
          }
        },
        {
          immediate: true,
        }
      )
    })

    expose({
      handleOpen: onOpen,
      handleClose: onClose,
      popperRefresh
    })

    return {
      ns,
      open,
      isSimple,
      onTrigger,
      onClick,
      onContextMenu,
      onMouseenter,
      onMouseleave,
      onClickOutside,
      dropdownRef,
      dropDownTextRef,
      dropDownContentRef,
      contentStyle,
      dropdownTriggerKls
    }
  }
})
</script>
